import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { BEFORE_AFTER } from "./data";
import { MoveHorizontal } from "lucide-react";

const Slider = ({ item }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  };
  return (
    <div className="relative">
      <div
        ref={ref}
        data-testid={`ba-slider-${item.id}`}
        className="relative w-full aspect-[3/2] rounded-3xl overflow-hidden select-none cursor-ew-resize bg-brand-blue"
        onMouseMove={(e) => dragging.current && move(e.clientX)}
        onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
        onMouseUp={() => (dragging.current = false)}
        onMouseLeave={() => (dragging.current = false)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
      >
        <img src={item.after} alt={`${item.label}: after cleaning`} className="absolute inset-0 h-full w-full object-cover pointer-events-none" />
        <span className="absolute top-4 right-4 z-20 rounded-full bg-brand-green px-3 py-1 text-xs font-bold text-brand-ink">AFTER</span>
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
          <img src={item.before} alt={`${item.label}: before cleaning`} className="absolute inset-0 h-full w-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} />
          <span className="absolute top-4 left-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-brand-blue">BEFORE</span>
        </div>
        <div className="absolute top-0 bottom-0 z-10 w-1 bg-white pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full bg-white shadow-lg text-brand-blue"><MoveHorizontal className="h-5 w-5" strokeWidth={2} aria-hidden="true" /></span>
        </div>
      </div>
      <label htmlFor={`ba-range-${item.id}`} className="sr-only">Reveal amount for {item.label} before and after comparison</label>
      <input id={`ba-range-${item.id}`} data-testid={`ba-range-${item.id}`} type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))} aria-valuetext={`${Math.round(pos)}% before image shown`} className="mt-4 w-full accent-brand-green cursor-pointer" />
    </div>
  );
};

export const BeforeAfter = () => {
  const [active, setActive] = useState(0);
  return (
    <section id="gallery" aria-labelledby="gallery-heading" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <span className="text-sm font-bold uppercase tracking-wide text-brand-accentink">The transformation</span>
              <h2 id="gallery-heading" className="mt-3 font-heading font-extrabold tracking-tight text-3xl md:text-4xl text-brand-blue">See the difference we make</h2>
            </div>
            <div role="tablist" aria-label="Before and after examples" className="flex flex-wrap gap-2">
              {BEFORE_AFTER.map((b, i) => (
                <button key={b.id} role="tab" aria-selected={active === i} data-testid={`ba-tab-${b.id}`} onClick={() => setActive(i)} className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${active === i ? "bg-brand-blue text-white" : "bg-brand-surface text-brand-ink2 hover:bg-brand-surface2"}`}>{b.label}</button>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}><Slider item={BEFORE_AFTER[active]} /></motion.div>
          <p className="mt-4 text-center text-sm text-brand-ink2">Drag the handle or use the slider to reveal the clean result.</p>
        </Reveal>
      </div>
    </section>
  );
};
